/**
 * Prev / Next pagination control for DRF cursor-paginated lists.
 *
 * Cursor pagination has no total count or page numbers, so this renders only
 * two directional buttons. Each is disabled when its adjacent page is absent.
 * Chevrons flip in RTL. Returns null when neither direction is available.
 */
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/utils";

interface CursorPaginationProps {
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Optional label rendered at the start (e.g. "Showing 25"). */
  info?: string;
  className?: string;
}

/** Shared visual for the two directional buttons — mirrors the table PageButton. */
const buttonClass = cn(
  "flex size-7 items-center justify-center rounded-[7px]",
  "border border-transparent bg-transparent text-[var(--text-tertiary)]",
  "transition-colors duration-100",
  "hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-gold)]",
  "disabled:opacity-40 disabled:pointer-events-none",
);

export function CursorPagination({
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  info,
  className,
}: CursorPaginationProps) {
  const { t } = useTranslation();
  if (!hasPrev && !hasNext) return null;

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3",
        "px-7 py-[14px] border-t border-[var(--divider)] bg-[var(--draft-bg)]",
        className,
      )}
    >
      {info ? (
        <span className="text-[11.5px] text-[var(--text-tertiary)]">{info}</span>
      ) : (
        <span />
      )}
      <div className="flex items-center gap-[3px]">
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasPrev}
          aria-label={t("common.previous")}
          className={buttonClass}
        >
          <ChevronLeft className="size-[14px] rtl:-scale-x-100" />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasNext}
          aria-label={t("common.next")}
          className={buttonClass}
        >
          <ChevronRight className="size-[14px] rtl:-scale-x-100" />
        </button>
      </div>
    </div>
  );
}
